import { useState } from "react";
import { Pencil } from "lucide-react";
import {
  dateKey,
  dateLabel,
  durationLabel,
  formatTime,
  type Appointment,
} from "../../shared/domain";
import { useStore } from "../store";
import { Modal } from "./ui";
import { AppointmentForm } from "./AppointmentForm";
import { AppointmentDelete } from "./AppointmentDelete";
import { WhatsAppAction } from "./WhatsAppAction";

export function AppointmentDetails({
  appointment: a,
  onClose,
  onDelete,
}: {
  appointment: Appointment;
  onClose: () => void;
  onDelete: (a: Appointment, until: number) => void;
}) {
  const { data, offline } = useStore();
  const s = data!.settings;
  const [editing, setEditing] = useState(false);
  if (editing)
    return (
      <AppointmentForm
        appointment={a}
        date={dateKey(a.start, s.timezone)}
        onClose={onClose}
        onDelete={onDelete}
      />
    );
  const end = a.start + a.duration * 60000;
  return (
    <Modal title="Appointment details" onClose={onClose}>
      <div className="appointment-details">
        <h3>{a.name}</h3>
        <dl className="appointment-facts">
          <div>
            <dt>Date</dt>
            <dd>
              {dateLabel(dateKey(a.start, s.timezone), {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </dd>
          </div>
          <div>
            <dt>Duration</dt>
            <dd>{durationLabel(a.duration)}</dd>
          </div>
          <div>
            <dt>Start time</dt>
            <dd>{formatTime(a.start, s)}</dd>
          </div>
          <div>
            <dt>End time</dt>
            <dd>
              {formatTime(end, s)}
              {dateKey(end, s.timezone) !== dateKey(a.start, s.timezone) && (
                <small> · {dateLabel(dateKey(end, s.timezone))}</small>
              )}
            </dd>
          </div>
          {a.phone && (
            <div>
              <dt>Phone number</dt>
              <dd>{a.phone}</dd>
            </div>
          )}
        </dl>
        <p className="muted">All times in {s.timezone}</p>
        {a.notes?.trim() && (
          <section aria-label="Appointment notes">
            <h4>Notes</h4>
            <p className="appointment-notes">{a.notes}</p>
          </section>
        )}
        <div className="details-actions">
          <button
            className="primary"
            disabled={offline}
            onClick={() => setEditing(true)}
          >
            <Pencil size={16} />
            Edit
          </button>
          <WhatsAppAction phone={a.phone} name={a.name} />
          <button type="button" onClick={onClose}>
            Close details
          </button>
        </div>
        <AppointmentDelete
          appointment={a}
          onClose={onClose}
          onDelete={onDelete}
        />
      </div>
    </Modal>
  );
}
